import OCButton from '@cx-web/components/primitives/button';
import Checkbox from '@cx-web/components/primitives/Checkbox';
import TextInput from '@cx-web/components/primitives/TextInput';
import { useLabelActions } from '@cx-web/feature-i18n';
import { useUIConfig } from '@cx-web/ui-shell/ui-config-context';
import classnames from 'classnames';

import { type OnboardingFetcher } from '~/routes/$/page-template/component-map.types';

import { useOnboardingActions } from './store/actions';
import { type OnboardingProps, onboardingVariants } from './store/types';
import styles from './Onboarding.module.css';

export { onboardingVariants, OnboardingProps };

export const Onboarding = ({ fetcher, ...props }: OnboardingProps & { fetcher: OnboardingFetcher }) => {
  const { hasMarginTop, variant } = props;

  const { resourcesUrl } = useUIConfig();

  const labelActions = useLabelActions();

  const {
    agreeTerms,
    desktopAndPrimary,
    disabled,
    errorOnBlur,
    handleInputBlur,
    handleInputChange,
    handleSubmit,
    input,
    inputIsValid,
    mobileAndSecondary,
    shouldRenderAsMyOCLoginCTA,
    shouldRenderAsSecondary,
    submitted,
    toggleTerms,
  } = useOnboardingActions({ ...props, variant }, fetcher);

  // Renderers

  const renderBgImg = (): JSX.Element => {
    const backgroundImage = shouldRenderAsSecondary ? 'nba' : 'nfl';
    const backgroundImageUrl = `${resourcesUrl}/assets/img/onboarding/signup-${backgroundImage}.webp`;

    return (
      <picture>
        <source srcSet={backgroundImageUrl} type="image/webp" />
        <img
          alt="onboarding-background"
          className={styles.bgImg}
          // @ts-expect-error: fetchpriority is not in React 18.2 types, but is valid HTML
          fetchpriority="high"
          loading="eager"
          src={backgroundImageUrl}
        />
      </picture>
    );
  };

  const renderTitle = (): JSX.Element => {
    return (
      <div className={styles.onboardingTitle}>
        <h2>{labelActions.get('SIGN_UP_WITH_ODDSCHECKER')}</h2>
        <h2 className={styles.highlight}>{labelActions.get('TO_BEAT_THE_ODDS')}</h2>
      </div>
    );
  };

  const renderDescription = (): JSX.Element => {
    return <div className={styles.description}>{labelActions.get('SUBSCRIBE_TO_EMAIL')}</div>;
  };

  const renderTextInput = (): JSX.Element => {
    return (
      <TextInput
        data-testid="onboarding-email-input"
        hasError={errorOnBlur}
        inputClassName={classnames(styles.input, errorOnBlur ? styles.error : '')}
        isValid={inputIsValid}
        name="email"
        onBlur={handleInputBlur}
        onChange={({ target }) => handleInputChange(target.value)}
        placeholder={labelActions.get('YOUR_EMAIL_ADDRESS')}
        shouldAutocomplete={false}
        type="email"
        value={input.value}
        wrapperClassName={styles.inputContainer}
      />
    );
  };

  const renderErrorBanner = (): JSX.Element | null => {
    if (!errorOnBlur) return null;

    return (
      <div className={styles.errorBanner} data-testid="error-banner">
        <div className={styles.errorTitle}>{labelActions.get('INVALID_EMAIL')}</div>
        <div className={styles.errorMessage}>{labelActions.get('EMAIL_ADDRESS_FORMAT')}</div>
      </div>
    );
  };

  const renderCTA = (): JSX.Element => {
    // TODO: add SIGNING_UP label 'Signing up...'
    const ctaText =
      fetcher.state === 'loading'
        ? labelActions.get('SIGNING_UP') || 'Signing up...'
        : shouldRenderAsMyOCLoginCTA || !submitted
          ? labelActions.get('SIGN_ME_UP')
          : labelActions.get('SIGNED_UP');

    return (
      <div className={styles.ctaWrapper}>
        <OCButton
          className={styles.ctaBtn}
          disabled={disabled}
          onClick={handleSubmit}
          testId="onboarding-submit"
          type="secondary"
        >
          {ctaText}
        </OCButton>
      </div>
    );
  };

  const renderLegalCheckbox = (): JSX.Element | null => {
    if (shouldRenderAsMyOCLoginCTA) return null;

    const termsAndPrivacyLabel = labelActions
      .get('YES_TO_TERMS')
      .replace(/%1/gi, labelActions.get('TERMS_URL'))
      .replace(/%2/gi, labelActions.get('PRIVACY_URL'));

    return (
      <Checkbox checked={agreeTerms} className={styles.terms} name="terms" onClick={toggleTerms}>
        <span
          className={styles.terms}
          dangerouslySetInnerHTML={{
            __html: termsAndPrivacyLabel,
          }}
          data-testid="onboarding-terms"
        />
      </Checkbox>
    );
  };

  return (
    <div
      className={classnames(
        styles.wrapper,
        shouldRenderAsSecondary ? styles.secondary : styles.primary,
        shouldRenderAsMyOCLoginCTA ? styles.isSignupCta : '',
        hasMarginTop ? styles.marginTop : '',
      )}
      data-testid="onboarding"
    >
      {renderBgImg()}
      <div className={styles.bgColor} />
      <div className={styles.contentWrapper}>
        {renderTitle()}
        <div className={styles.formContainer}>
          {renderDescription()}
          <div className={styles.inputWrapper}>
            {renderTextInput()}
            {mobileAndSecondary ? renderErrorBanner() : null}
            {renderCTA()}
          </div>
          {desktopAndPrimary ? renderErrorBanner() : null}
          {renderLegalCheckbox()}
        </div>
      </div>
    </div>
  );
};
